import { BaseDriver } from './device'
import { bytesPadding64, delay, numberToRgb, printHex } from '@libs/utils/tools'
import { musicData } from '@libs/music/music'
import { ResDeviceState } from '@libs/types/response'
import { mg63Magnet } from './magent/mg63'
import { DeviceProtocol } from '@hid/device/device_model'
import { MusicCaptureStatus, SpecificItem } from '@libs/types/enum'
import { MG63KeyboardParams, mg63ReportMap } from './data_conversion/mg63'
import { KIO, KK, OutputMode } from '@hid/define/define'
import { allKeysForDevice, DVKFromVK, HIDFromVk, kkFromVK } from '@hid/define/define_fn'
import { deviceManager } from '@hid/device/device_manager'
import { defaultVK } from '@hid/define/define_keys'
import { splitIntoBytes } from './tool'
import defineType from '@hid/define/define_type'
import { lightMatrix } from '@hid/define/define_light_matrix'

interface MacroConfig {
  actionCount: number
  repeatCount: number
  macroType: number
}

export class mg63Driver extends BaseDriver {
  musicIns: musicData
  private customMode = 0xf1 // 自定义模式 0xf1 ~ 0xf4
  declare magnet: mg63Magnet | undefined
  public isBusy = false
  constructor(public protocol: DeviceProtocol) {
    super(protocol)
    this.musicIns = new musicData(this.onMusicData)
    this.magnet = new mg63Magnet(protocol, this)
  }

  private onMusicData = async (data: number[]) => {
    // computeFPS()
    if (this.isBusy) return
    if (this.save.ColorsDynamic.Run == false) {
      return
    }
    if (data.length > 0) {
      await this.sendEffectData(data)
    }
  }

  private async sendEffectData(data: number[]) {
    const keyMap = lightMatrix(deviceManager.deviceID)
    const packets: number[][] = []
    let currentPacket: number[] = [0xb5]

    // 构建数据包
    for (let i = 0; i < keyMap.size; i++) {
      const k = keyMap.get(i)
      if (k === undefined || k === 0) {
        continue
      }

      // 检查当前包是否已满 (Key + 3 Colors = 4 bytes)
      if (currentPacket.length + 4 > 64) {
        packets.push(currentPacket)
        currentPacket = [0xb5]
      }

      // 颜色格式: Key, R, G, B
      currentPacket.push(k, data[i * 3], data[i * 3 + 1], data[i * 3 + 2])
    }
    if (currentPacket.length > 1) {
      packets.push(currentPacket)
    }

    // 发送数据包
    for (const packet of packets) {
      const toSend = bytesPadding64(packet, 64, [0x00])
      // 发送时不等待回复 (haveResult = false)，避免阻塞
      this.DeviceHID.send(toSend, false, false)
    }
  }

  async getVersion(): Promise<string> {
    if (this.magnet != null) {
      await this.magnet.initPromise
    }
    const data = Array(64).fill(0)
    data[0] = 0xe5
    const res = await this.DeviceHID.send(data)
    const version = `${res[1]}.${res[2].toString(16).padStart(2, '0').toUpperCase()}`
    return version
  }

  private async turnOnColorCustom() {
    const lightMode = this.save.LightMode
    const data = Array(64).fill(0)
    data[0] = 0x10
    data[1] = this.customMode
    data[2] = 0x09
    data[3] = lightMode.Light
    await this.DeviceHID.send(data)
  }
  // 写入自定义颜色
  private async writeCustomColor() {
    const colorsCustom = this.save.ColorsCustom
    const keyMap: Map<KK, number[]> = new Map()
    if (colorsCustom.size == 0) {
      const keys = allKeysForDevice(deviceManager.deviceID)
      keys.forEach((key) => keyMap.set(key, [0, 0, 0]))
    }
    for (const [key, color] of colorsCustom) {
      keyMap.set(key, [(color >> 16) & 0xff, (color >> 8) & 0xff, color & 0xff])
    }
    await this.cmdSet([0x13, 0x01, 0x0f, keyMap.size], keyMap, 200, true, false)
  }
  private _customColorLock = false
  private _customColorPending = false

  async syncCustomColor(): Promise<void> {
    if (this._customColorLock) {
      // 正在执行，标记有新请求
      this._customColorPending = true
      return
    }
    this._customColorLock = true
    do {
      this._customColorPending = false
      await this.musicIns.musicCapturePause()
      await this.turnOnColorCustom()
      await delay(150)
      await this.writeCustomColor()
      this.musicIns.musicCaptureContinue()
      // 如果执行期间有新请求，则再执行一次（只保留最后一次）
    } while (this._customColorPending)
    this._customColorLock = false
  }

  async syncLeds(): Promise<void> {
    await this.musicIns.musicCapturePause()

    const lightMode = this.save.LightMode
    const data = Array(64).fill(0)
    data[0] = 0x10
    data[1] = lightMode.Mode
    data[2] = lightMode.FullColor ? 0x08 : 0x09
    data[3] = lightMode.Light
    let speed = lightMode.Speed
    if (typeof lightMode.Direction !== 'undefined') {
      if (lightMode.Direction === 1) {
        speed = speed | 0x80
      }
    }
    data[4] = speed

    if (lightMode.FullColor == false) {
      const { r, g, b } = numberToRgb(lightMode.Color)
      data[5] = g
      data[6] = r
      data[7] = b
    }
    await this.DeviceHID.send(data)
    console.log('发送灯光数据:', data)
    this.musicIns.musicCaptureContinue()
  }

  async syncKeys(): Promise<void> {
    await this.musicIns.musicCapturePause()
    const keys = this.save.Keys
    for (const key of keys) {
      if (key.Output.Mode != OutputMode.Key) {
        continue
      }
      const data = Array(64).fill(0)
      data[0] = 0x20
      data[1] = key.Input.WithFN ? 0x01 : 0x00
      data[2] = key.Input.KK
      if (key.Output.VK == defaultVK) {
        data[3] = key.Input.KK
      } else {
        if (defineType.isMedia(key.Output.VK) || defineType.isMouse(key.Output.VK)) {
          const [lowByte, highByte] = splitIntoBytes(DVKFromVK(key.Output.VK, deviceManager.deviceID))
          data[3] = lowByte
          data[4] = highByte
        } else {
          const kk = kkFromVK(key.Output.VK, deviceManager.deviceID)
          // TODO: 兼容HOME/Del键实际键盘中没有,导致找不到KK的问题,后期需优化
          if (kk == undefined) {
            data[3] = HIDFromVk(key.Output.VK) ?? key.Output.VK
          } else {
            data[3] = kk
          }
        }
      }
      // 多媒体：0x04
      // 鼠标：0x07
      // 宏：0x80
      if (defineType.isMedia(key.Output.VK)) {
        data[6] = 0x04
      } else if (defineType.isMouse(key.Output.VK)) {
        data[6] = 0x07
      }
      await this.DeviceHID.send(data)
      await delay(150)
    }
    await this.magnet?.clearKME()
    this.musicIns.musicCaptureContinue()
  }
  private macroTypeConfig(params: KIO): [number, number] {
    const ext = params.Output.Extension || []
    const mode = (ext[0] ?? 0) & 0xff
    const value = (ext[1] ?? 0) & 0xff

    let repeatCount = 0
    let macroType = mode

    // mode = 0：不循环
    if (mode === 0) {
      repeatCount = 0
    }
    // mode = 1：固定次数循环（value 表示次数，从 1 开始）
    if (mode === 1) {
      macroType = 0
      repeatCount = (value - 1) & 0xff
    }
    return [repeatCount & 0xff, macroType & 0xff]
  }

  // 组装宏数据
  private buildMacroConfig(): [number[], Map<KK, MacroConfig>, Map<KK, MacroConfig>] {
    const macros = this.save.Macros // Map<number, Record[]>
    const keys = this.save.Keys
    const macroMap: Map<KK, MacroConfig> = new Map()
    const macroData: number[] = []
    let actionCount = 0
    const fn_macroMap: Map<KK, MacroConfig> = new Map()
    for (const item of keys) {
      if (item.Output.Mode != OutputMode.Macro) continue

      const macro = macros.get(item.Output.DataIndex)
      if (!macro || macro.length == 0) continue

      const params: number[] = []
      for (let i = 0; i < macro.length; i++) {
        const rec = macro[i]
        const delay = rec.Delay ?? 0

        let keyType = 0x02 // 普通按键
        let hid = HIDFromVk(rec.Key) ?? rec.Key
        if (defineType.isModifier(rec.Key)) keyType = 0x01
        if (defineType.isMouse(rec.Key)) keyType = 0x03

        if (!rec.Press) keyType |= 0x80
        if (i === macro.length - 1) keyType |= 0xc0

        // 具体延时时间==((延时1&0x6F)<<24+延时2<<16+类型<<8+HID)ms
        // 单个延时code最大延时时间 0x6FFF*1MS = 28671ms
        params.push(
          (delay >> 8) & 0x6f, // 延时高7位
          delay & 0xff, // 延时低8位
          keyType & 0xff, // 类型
          hid & 0xff, // HID
        )
        if (defineType.isMouse(rec.Key) && i === macro.length - 1) {
          // TODO:假如宏的最后一个动作为鼠标光标数据，则需要再补一个结束宏：0x00 0x00 0xC0 0x00
          params.push(0x00, 0x00, 0xc0, 0x00)
        }
      }
      macroData.push(...params)
      const [repeatCount, macroType] = this.macroTypeConfig(item)
      const config: MacroConfig = {
        actionCount: actionCount,
        repeatCount: repeatCount,
        macroType: macroType,
      }
      if (item.Input.WithFN) {
        fn_macroMap.set(item.Input.KK, config)
      } else {
        macroMap.set(item.Input.KK, config)
      }
      actionCount += params.length
    }
    return [macroData, macroMap, fn_macroMap]
  }
  // 写入宏数据
  private async writeMacroData(macro: number[]) {
    const payloadCapacity = 64 - 4
    const totalPages = Math.ceil(macro.length / payloadCapacity)
    for (let page = 0; page < totalPages; page++) {
      const data = Array(64).fill(0)
      data[0] = 0x60 // CMD
      data[1] = page // 当前页序号/偏移
      data[2] = totalPages // 总页数

      const offset = page * payloadCapacity
      const chunk = macro.slice(offset, offset + payloadCapacity)
      for (let i = 0; i < chunk.length; i++) {
        data[4 + i] = chunk[i]
      }
      await this.DeviceHID.send(data)
      await delay(50)
    }
  }

  // 按键设置宏
  private async setMacroForKeys(macroMap: Map<KK, MacroConfig>, isFn: boolean) {
    for (const [key, item] of macroMap) {
      const data = Array(64).fill(0)
      data[0] = 0x20
      data[1] = isFn ? 0x01 : 0x00
      data[2] = key

      data[3] = (item.actionCount >> 8) & 0xff // 动作总数
      data[4] = item.actionCount & 0xff

      data[5] = item.repeatCount
      data[6] = 0x80 | item.macroType
      await this.DeviceHID.send(data)
      await delay(100)
    }
  }

  async syncMacros(): Promise<void> {
    await this.musicIns.musicCapturePause()

    const [macroData, macroMap, fn_macroMap] = this.buildMacroConfig()
    await this.writeMacroData(macroData)

    // TODO:普通层和fn 层需要分开设置，否则数据会错乱
    await this.setMacroForKeys(macroMap, false)
    await this.setMacroForKeys(fn_macroMap, true)

    await this.magnet?.clearKME()
    // await delay(100)

    this.musicIns.musicCaptureContinue()
  }

  async syncConfig() {
    this.setAllSpecificItem()
  }
  // 优化后的 setAllSpecificItem，只需同步一次
  private async setAllSpecificItem() {
    const items = this.save.Items
    if (items.size === 0) return
    const entry = items.entries().next()
    if (entry.done) return
    const [key, item] = entry.value
    await this.setSpecificItem(key, item)
  }
  getDeviceState(): Promise<ResDeviceState> {
    throw new Error('Method not implemented.')
  }

  async reset(): Promise<void> {
    const data = Array(64).fill(0)
    data[0] = 0x52
    data[1] = 0x00
    data[2] = 0x01
    data[3] = 0xff //FF:所有层都恢复
    data[4] = 0x01
    data[5] = 0x01
    data[6] = 0x01
    data[7] = 0x01
    await this.DeviceHID.send(data)
  }

  private getValFromItems(item: SpecificItem, key: SpecificItem, value: any): any {
    const items = this.save.Items
    return key == item ? value : items.get(key)
  }

  // 修复后的 setSpecificItem，从 items 中读取全量数据
  private async setSpecificItem(item: SpecificItem, value: any) {
    const data = Array(64).fill(0)
    data[0] = 0x50

    // byte1: 回报率
    const pollingRate = this.getValFromItems(item, SpecificItem.PollingRate, value)
    let setIndex = 1
    mg63ReportMap.forEach((v, index) => {
      if (v == pollingRate) setIndex = index
    })
    data[1] = setIndex

    console.log('setSpecificItem', item, value)
    data[2] = this.getValFromItems(item, SpecificItem.SleepTime, value) ?? 0xff // 一级休眠
    data[3] = this.getValFromItems(item, SpecificItem.SleepTime, value) ?? 0xff // 一级休眠
    data[4] = 0 // 系统设置默认 win

    data[5] = this.getValFromItems(item, SpecificItem.LockWin, value) || 0
    data[6] = this.getValFromItems(item, SpecificItem.LockKeyboard, value) || 0
    data[7] = this.getValFromItems(item, SpecificItem.DirectionKeySwap, value) || 0
    data[8] = this.getValFromItems(item, SpecificItem.KeyMode, value) || 0
    data[9] = this.getValFromItems(item, SpecificItem.BacklightClose, value) || 0
    data[10] = this.getValFromItems(item, SpecificItem.SideLedClose, value) || 0
    data[11] = this.getValFromItems(item, SpecificItem.SideLedClose, value) || 0
    data[12] = this.getValFromItems(item, SpecificItem.CancelDefaultDeadZone, value) || 0

    await this.DeviceHID.send(data)
    await delay(50)
  }

  private async readSpecificItem(item: SpecificItem) {
    const data = Array(64).fill(0)
    data[0] = 0xe1

    if (item == SpecificItem.PollingRate) {
      data[3] = MG63KeyboardParams.ReportRate_OFFSET
      const res = await this.DeviceHID.send(data)
      return mg63ReportMap.get(res[20]) || 1000
    }

    if (item == SpecificItem.SleepTime) {
      data[3] = MG63KeyboardParams.FirstSleepTime_OFFSET
      const res = await this.DeviceHID.send(data)
      printHex(res)
      console.log('FirstSleepTime_OFFSET', res[37])
      return res[37]
    }

    if (item == SpecificItem.LockWin) {
      data[3] = MG63KeyboardParams.Winlock_OFFSET
      const res = await this.DeviceHID.send(data)
      return res[27]
    }

    if (item == SpecificItem.LockKeyboard) {
      data[3] = MG63KeyboardParams.KBlock_OFFSET
      const res = await this.DeviceHID.send(data)
      return res[28]
    }
    if (item == SpecificItem.DirectionKeySwap) {
      data[3] = MG63KeyboardParams.DirectionKeySwap
      const res = await this.DeviceHID.send(data)
      return res[29]
    }

    if (item == SpecificItem.BacklightClose) {
      data[3] = MG63KeyboardParams.CustomLedEnable_OFFSET
      const res = await this.DeviceHID.send(data)
      return res[33]
    }
    if (item == SpecificItem.SideLedClose) {
      data[3] = MG63KeyboardParams.SideLedMode_OFFSET
      const res = await this.DeviceHID.send(data)
      return res[34]
    }
    if (item == SpecificItem.KeyMode) {
      data[3] = MG63KeyboardParams.KeyMode
      const res = await this.DeviceHID.send(data)
      return res[42]
    }
    if (item == SpecificItem.CancelDefaultDeadZone) {
      data[3] = MG63KeyboardParams.CancelDefaultDeadZone
      const res = await this.DeviceHID.send(data)
      return res[44]
    }
  }

  async setOrReadSpecificItem(item: SpecificItem, isWrite: boolean, value: any): Promise<any> {
    if (isWrite) {
      return this.setSpecificItem(item as SpecificItem, value)
    }
    return this.readSpecificItem(item)
  }
  pair24g(): Promise<number> {
    throw new Error('Method not implemented.')
  }
  updateFirmware(_: number[]): Promise<boolean> {
    throw new Error('Method not implemented.')
  }
  async syncDecorationLed(): Promise<void> {
    const decorates = this.save.Decorates
    if (this.changeProfileFlag && (decorates == undefined || decorates.length == 0)) {
      return
    }

    if (decorates == undefined || decorates.length == 0) {
      throw new Error('Decorates is undefined')
    }
    await this.musicIns.musicCapturePause()
    for (let i = 0; i < decorates.length; i++) {
      const decorate = decorates[i]
      const data = Array(64).fill(0)
      data[0] = 0x11
      data[1] = decorate.LightMode.Mode
      data[2] = decorate.LightMode.FullColor ? 0x08 : 0x09
      data[3] = decorate.LightMode.Light
      let speed = decorate.LightMode.Speed
      if (typeof decorate.LightMode.Direction !== 'undefined') {
        if (decorate.LightMode.Direction === 1) {
          speed = speed | 0x80
        }
      }
      data[4] = speed

      if (decorate.LightMode.FullColor == false) {
        const { r, g, b } = numberToRgb(decorate.LightMode.Color)
        data[5] = g
        data[6] = r
        data[7] = b
      }
      await this.DeviceHID.send(data)
      await delay(150)
    }
    this.musicIns.musicCaptureContinue()
  }
  async syncDynamicColor(): Promise<boolean> {
    const colorsDynamic = this.save.ColorsDynamic
    if (this.changeProfileFlag && colorsDynamic.Run == false) {
      return true
    }

    let state = MusicCaptureStatus.Stop
    if (colorsDynamic.Run) {
      // if (!this.save.LightLastIsCustom) {
      //   await this.turnOnColorCustom()
      // }
      state = MusicCaptureStatus.Start
    }
    return await this.setDynamicColorState(state)
  }

  private async setDynamicColorState(state: MusicCaptureStatus) {
    this.musicIns.setColorsDynamic(this.save.ColorsDynamic)
    return await this.musicIns.dynamicColorState(state)
  }

  async cmdSet(cmd: number[], sets: Map<KK, number[]>, delayValue: number = 100, isAwait: boolean = true, needCalculateSetNum: boolean = true) {
    if (sets.size == 0) return new Map<KK, [number, number]>()
    let index = 0
    let prefixed = [...cmd]
    const data: number[][] = [[...prefixed]]
    const counts: number[] = [0]
    sets.forEach((v, k) => {
      if (data[index].length >= 64 - v.length) {
        index++
        data[index] = [...prefixed]
        counts[index] = 0
      }
      data[index].push(k, ...v)
      counts[index]++
    })
    for (let i = 0; i < data.length; i++) {
      if (needCalculateSetNum) {
        data[i][1] = counts[i]
      }
      const toSend = bytesPadding64(data[i], 64, [0x00])
      // printHex(toSend)
      if (isAwait) {
        await this.DeviceHID.send(toSend)
        await delay(delayValue)
      } else {
        this.DeviceHID.send(toSend)
      }
    }
  }

  async send(data: number[]) {
    return await this.DeviceHID.send(data)
  }
  destroy() {
    this.magnet?.destroy()
  }
}
